const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

let initialized = false;
let loading = false;
let deferredSetupDone = false;
const pendingEvents = [];

function flushPendingEvents() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  while (pendingEvents.length > 0) {
    const [name, params] = pendingEvents.shift();
    window.gtag('event', name, params);
  }
}

function loadAnalytics() {
  if (!GA_MEASUREMENT_ID || initialized || loading || typeof window === 'undefined') {
    return;
  }

  // Tests (and any pre-injected gtag) can send without waiting on the remote script.
  if (typeof window.gtag === 'function' && !document.querySelector('script[src*="googletagmanager.com/gtag"]')) {
    initialized = true;
    window.dataLayer = window.dataLayer || [];
    flushPendingEvents();
    return;
  }

  loading = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  script.onload = () => {
    initialized = true;
    loading = false;
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
    });
    flushPendingEvents();
  };
  script.onerror = () => {
    loading = false;
  };
  document.head.appendChild(script);
}

/**
 * Defer gtag until the browser is idle or the user interacts — keeps it off the critical path.
 */
export function initializeAnalytics() {
  if (!GA_MEASUREMENT_ID || deferredSetupDone || typeof window === 'undefined') {
    return;
  }

  deferredSetupDone = true;

  const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
  let idleId = null;
  let timeoutId = null;
  let settled = false;

  const cleanup = () => {
    if (settled) return;
    settled = true;
    interactionEvents.forEach((eventName) => {
      window.removeEventListener(eventName, trigger, { capture: true });
    });
    if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId);
    }
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  };

  const trigger = () => {
    cleanup();
    loadAnalytics();
  };

  interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, trigger, { once: true, passive: true, capture: true });
  });

  if (typeof window.requestIdleCallback === 'function') {
    idleId = window.requestIdleCallback(trigger, { timeout: 4000 });
  } else {
    timeoutId = window.setTimeout(trigger, 3500);
  }
}

function sendEvent(name, params = {}) {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') {
    return;
  }

  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params);
    return;
  }

  pendingEvents.push([name, params]);
  loadAnalytics();
}

function currentPagePath() {
  if (typeof window === 'undefined') {
    return '';
  }

  return `${window.location.pathname}${window.location.search}`;
}

export function calculateScrollDepth({ scrollY, innerHeight, scrollHeight }) {
  if (!scrollHeight || scrollHeight <= innerHeight) return 100;
  return Math.min(100, Math.max(0, Math.round(((scrollY + innerHeight) / scrollHeight) * 100)));
}

export function trackPageView(path, title) {
  sendEvent('page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });
}

export function trackSectionView(sectionName, params = {}) {
  sendEvent('section_view', {
    section_name: sectionName,
    page_path: currentPagePath(),
    ...params,
  });
}

export function trackScrollDepth(depth) {
  sendEvent('scroll_depth', {
    percent_scrolled: depth,
    page_path: currentPagePath(),
  });
}

function getElementText(element) {
  const label = element.getAttribute('data-analytics-label') || element.getAttribute('aria-label');
  if (label) {
    return label.trim();
  }

  return (element.innerText || element.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 120);
}

function getClickType(element) {
  if (element.getAttribute('href')?.includes('apps.apple.com')) {
    return 'app_store';
  }

  if (element.tagName === 'A') {
    return element.hostname && element.hostname !== window.location.hostname ? 'outbound_link' : 'internal_link';
  }

  return element.tagName.toLowerCase();
}

export function trackSiteClick(element) {
  const href = element.getAttribute('href') || '';
  const clickType = getClickType(element);
  const configuredEventName = element.getAttribute('data-analytics-click');
  const eventName = configuredEventName || (clickType === 'app_store' ? 'app_store_click' : 'site_click');

  const appStoreCampaign = clickType === 'app_store'
    ? new URL(href, window.location.origin).searchParams.get('ct') || ''
    : '';
  const analyticsValue = element.getAttribute('data-analytics-value') || '';

  sendEvent(eventName, {
    click_text: getElementText(element),
    click_url: href,
    click_type: clickType,
    click_location: element.getAttribute('data-analytics-location') || '',
    ...(appStoreCampaign ? { app_store_campaign: appStoreCampaign } : {}),
    ...(analyticsValue ? { content_value: analyticsValue } : {}),
    page_path: currentPagePath(),
  });
}

export function trackFormSubmit(formName, status, params = {}) {
  const normalizedFormName = String(formName || 'form')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'form';

  sendEvent(`${normalizedFormName}_form_submit`, {
    form_name: normalizedFormName,
    submit_status: status,
    page_path: currentPagePath(),
    ...params,
  });
}
